<div class="reading-page doc-page">
  <div id="head" class="section-header">
    <h2 class="section-title">Tidy Desktop Privacy Policy</h2>
  </div>
  <app-policy-card [policyBody]="body">
    <!-- Body -->
    <ng-template #body>
      <div class="policy-content">
        <p><strong>Effective: 10 October 2026</strong></p>

        <p><strong>Who we are</strong></p>
        <p><a [routerLink]="URLS.plugins.macosTidyDesktop">Tidy Desktop: AI Organizer</a> is made by <strong>Codigrate OÜ</strong>,
          registry code 16262630, Sepapaja tn 6, 15551 Tallinn, Estonia. You can reach us at
          <a [href]="'mailto:' + environmentCommon.company.email">{{ environmentCommon.company.email }}</a>.</p>

        <p><strong>No personal data</strong></p>
        <p>Tidy Desktop collects no personal data. There is no account, no analytics, no advertising, no tracking and no
          third-party SDKs. The app makes no network requests of its own: macOS gives it no network access, so it cannot
          connect to the internet, and Codigrate receives no data from it.</p>

        <p><strong>Outside the app</strong></p>
        <p>Two things outside the app can still reach us. The codigrate.com link in Settings › About opens our website in
          your browser, where the <a [routerLink]="URLS.privacyPolicy">Codigrate Privacy Policy</a> applies. If you let your
          Mac share crash data with app developers (System Settings › Privacy &amp; Security › Analytics &amp; Improvements),
          Apple may pass the app's crash reports and anonymous usage statistics on to us. Anything you send with the Share
          menu, and files kept in iCloud Drive, are handled by macOS and Apple under Apple's privacy policy.</p>

        <p><strong>What the app reads on your Mac</strong></p>
        <p>To show your desktop in collections, Tidy Desktop reads the files in your Desktop folder: their names, kinds,
          dates and icons, Quick Look thumbnails (small previews macOS draws from a file's contents, kept only in memory),
          whether a file is a screenshot, and the website a downloaded file came from (macOS's "Where from" information,
          of which only the site's name is used). Finder tags, sizes and image dimensions are read only when you open a
          file's right-click menu or Get Info.</p>
        <p>To tell whether Finder is showing its own desktop icons, the app checks which apps own the windows on your
          screen (only their app names and window layers, never window titles or contents). It also notices when System
          Settings closes, so it can bring its own window back, and lists the apps that can open a file for Open With.
          None of this is kept or sent anywhere.</p>
        <p>A Locations window does the same for the folder it shows: Downloads, Documents, Pictures, Music, Movies or
          Applications. macOS asks for the Desktop, Downloads and Documents with its own permission prompt, and you can
          change that answer at any time in System Settings › Privacy &amp; Security › Files &amp; Folders. Pictures, Music
          and Movies are opened to the app by macOS when it is installed, and every app can read Applications, so macOS
          shows no prompt for these four; the app reads one only while that Location is turned on.</p>

        <p><strong>Your files</strong></p>
        <p>Collections are a view, not folders: putting a desktop file in a collection changes nothing on disk, and the app
          never moves, renames or deletes a file on its own. A file changes only when you ask: with a command from its
          right-click menu (such as Rename, Move to Trash, Compress, Duplicate, Make Alias or Tags) or with a drag and
          drop. Dropping a file from another folder onto a collection, or choosing Move to Collection in a Locations
          window, puts that file in your Desktop folder the way Finder does: moved when it comes from the same disk,
          copied from another disk, with Option held or when macOS does not allow the move. Dropping a file onto a
          Locations window puts it in that folder the same way; the Applications window is read only.</p>
        <p>When you compress several files at once, the app copies them into a temporary folder on the same disk while it
          makes the zip, and deletes that folder as soon as the zip is done. When you choose Copy or drag a file out of a
          collection, the app hands macOS a reference to that file for the place you paste or drop it; it never reads
          what is already on your clipboard.</p>
        <p>Tidy Desktop never sets your desktop picture, except to put your own picture back once on a Mac where an early
          test version of the app had replaced it.</p>

        <p><strong>Apple Intelligence</strong></p>
        <p>AI Grouping uses Apple's on-device model. For each file it is shown the file's name, its kind and, for a
          downloaded file, the name of the website it came from, never a file's contents. When you make a collection of
          your own, the model is also shown its name and the description you typed for it. It is also told whether a file
          is a screenshot and which language your Mac uses, so it can name your collections in that language. Its answers
          are kept on your
          Mac, so a later regroup only asks about files it has not answered about yet, such as new or renamed ones.
          Nothing is sent to Codigrate or anyone else. Without Apple Intelligence the app groups files by kind, and your
          own collections take the files whose names have their words, on your Mac as well.</p>

        <p><strong>Where the app keeps its data</strong></p>
        <p>Your collections, their layout, your settings and the model's answers are kept in the app's own folder on your
          Mac (its sandbox container), and nowhere else: no iCloud, no Keychain, no shared containers. This includes the
          names of the files on your desktop and in any Locations window you turn on, and what the model said each file is
          about, such as a place, a company or a person. Answers for files you have moved or deleted are kept until there
          are more than 1,000 of them, so a file that comes back is placed without asking again. If a file action you
          ask for fails, the file's name may appear with the error in macOS's own system log on your Mac, as it does for
          other apps. Opening at login is optional, stays off until you turn it on, and is registered with macOS on your
          Mac.</p>

        <p><strong>Purchases</strong></p>
        <p>Tidy Desktop is bought once, through the Mac App Store, under Apple's terms and privacy policy. There are no
          in-app purchases, and Codigrate never sees your payment details.</p>

        <p><strong>Developer tools</strong></p>
        <p>The app contains a few developer test tools, used to make its App Store screenshots and to diagnose problems.
          They run only when the app is started from Terminal with a special developer option, never when you open it
          normally, and anything they produce stays on your Mac.</p>

        <p><strong>Access, export and deletion</strong></p>
        <p>To erase everything the app keeps on your Mac, choose Reset in Settings › Access, or delete the folder
          ~/Library/Containers/com.codigrate.tidydesktop after removing the app. The folder permissions you gave stay in
          System Settings › Privacy &amp; Security › Files &amp; Folders until you change them there.</p>

        <p>Because nothing is collected, there is nothing for Codigrate to access, export or delete. If you have any
          questions, write to us at
          <a [href]="'mailto:' + environmentCommon.company.email">{{ environmentCommon.company.email }}</a>.</p>

        <p><strong>Changes to this policy</strong></p>
        <p>If this policy changes, the new version will be posted on this page with a new effective date.</p>

        <p><strong>Terms of Use</strong></p>
        <p>Tidy Desktop's Terms of Use are Apple's standard
          <a [href]="eulaUrl" target="_blank" rel="noopener">Licensed Application End User License Agreement</a>.</p>
      </div>
    </ng-template>
  </app-policy-card>
</div>
